import { Location } from '@angular/common';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  Component,
  ComponentRef,
  effect,
  EnvironmentInjector,
  inject,
  inputBinding,
  OnDestroy,
  OnInit,
  outputBinding,
  runInInjectionContext,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Analytics } from '@core/services/analytics';
import { ApiClient } from '@core/services/api-client';
import { ProctoringService } from '@core/services/proctoring';
import { CodeEditor } from '@shared/components/code-editor/code-editor';
import { Select, SelectItem } from '@shared/components/select/select';
import { TestCaseCard } from '@shared/components/test-case-card/test-case-card';
import { Timer, TimerConfig } from '@shared/components/timer/timer';
import { InviteData } from '@shared/models/invitation';
import { Problem, ProblemStorage } from '@shared/models/problem';
import { TestCaseResult } from '@shared/models/test-case';

@Component({
  selector: 'app-problem-page',
  imports: [TestCaseCard, Select],
  templateUrl: './problem-page.html',
})
export class ProblemPage implements OnInit, OnDestroy {
  // ===== DI =====
  private readonly location = inject(Location);
  private readonly apiClient = inject(ApiClient);
  private readonly route = inject(ActivatedRoute);
  private readonly injector = inject(EnvironmentInjector);
  private readonly router = inject(Router);
  private readonly analytics = inject(Analytics);
  private readonly proctor = inject(ProctoringService);

  // ===== View children =====
  readonly timerContainer = viewChild('timerOutlet', {
    read: ViewContainerRef,
  });
  readonly tokenTimerContainer = viewChild('tokenTimerOutlet', {
    read: ViewContainerRef,
  });
  readonly codeEditorContainer = viewChild('codeEditorOutlet', {
    read: ViewContainerRef,
  });

  // ===== State =====
  private problemId = '';
  private readonly invitedToken = signal<string>('');
  private readonly email = signal<string>('');

  readonly problem = signal<Problem | null>(null);

  // Editor
  readonly codeEditorLanguage = signal<string>('');
  readonly codeEditorInput = signal<string>('');
  readonly codeEditorOutput = signal<string>('');
  private codeEditorRef: ComponentRef<CodeEditor> | null = null;

  // Language selector
  readonly languageItems = signal<SelectItem[]>([]);
  readonly selectedLanguage = signal<SelectItem>({ value: '', label: '' });

  // Test execution
  readonly testCaseDetail = signal<TestCaseResult[]>([]);
  readonly running = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly disabled = signal<boolean>(true);

  // Timers
  private expireAt: Date | null = null;
  private tokenExpireAt: Date | null = null;
  private hasExpired = false;
  private readonly timerConfig = signal<TimerConfig | null>(null);
  private readonly tokenTimerConfig = signal<TimerConfig | null>(null);
  private timerRef: ComponentRef<Timer> | null = null;
  private tokenTimerRef: ComponentRef<Timer> | null = null;

  // ===== Lifecycle =====
  async ngOnInit(): Promise<void> {
    await this.initializeInvitedToken(); // sets token/email + proctor context(testSetId)
    await this.subscribeToRouteParams(); // sets problemId + proctor.setCurrentProblemId
    await this.setUpProblemState();
    await this.validateExpirationData();

    await this.analytics.configureUserTracking();
    this.proctor.start();

    await this.fetchProblemData();
    await this.setUpComponents();
    await this.initializeCode();

    this.setUpAnalytics();
  }

  ngOnDestroy(): void {
    this.proctor.stop();
    this.cleanup();
  }

  // ===== Public handlers =====
  async handleRunTest(): Promise<void> {
    await this.executeCodeOperation('runTest');
  }

  async handleSubmit(): Promise<void> {
    await this.executeCodeOperation('submitTest');
  }

  onLanguageChange(): void {
    const newLangId = this.selectedLanguage().value;
    const newLangName = this.selectedLanguage().label;

    this.codeEditorLanguage.set(newLangName);

    try {
      const problemResults = this.getProblemResults();
      const savedProblem = problemResults.find(
        p => p.problemId === this.problemId
      );

      if (savedProblem && savedProblem.language === newLangId) {
        this.codeEditorInput.set(savedProblem.code);
      } else {
        this.codeEditorInput.set(this.getBaseCode(newLangId));
      }
    } catch {
      this.codeEditorInput.set(this.getBaseCode(newLangId));
    }
  }

  // ===== Init & Setup =====
  private async initializeInvitedToken(): Promise<void> {
    try {
      const inviteData = localStorage.getItem('inviteData');
      if (!inviteData) return this.redirectToPath('/forbidden');

      const parsed: InviteData = JSON.parse(inviteData);
      if (!this.isValidInviteData(parsed))
        return this.redirectToPath('/forbidden');

      this.invitedToken.set(parsed.data.invitedToken);
      this.email.set(parsed.data.email);

      // set proctoring context (page + testSetId)
      // ถ้า inviteData มี testSetId ให้ผูกไว้เพื่อเติม context ทุก event
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const testSetId = (parsed as any)?.data?.testSetId ?? null;
      this.proctor.setContext('problem', testSetId);
    } catch {
      this.redirectToPath('/forbidden');
    }
  }

  private async subscribeToRouteParams(): Promise<void> {
    this.route.params.subscribe(params => {
      const problemId = params['problemId'];
      if (!problemId) return this.redirectToPath('/forbidden');

      this.problemId = problemId;
      this.proctor.setCurrentProblemId(this.problemId);
    });
  }

  private async setUpProblemState(): Promise<void> {
    this.resetExpirationData();
    const headers = new HttpHeaders()
      .set('authorization', `Bearer ${this.invitedToken()}`)
      .set('x_problem_id', this.problemId);

    try {
      const response = await this.apiClient.problemsState(headers);
      if (response?.expiredAt) {
        const expiredAt = new Date(response.expiredAt);
        const tokenExpiredAt = new Date(response.tokenExpiredAt);
        this.expireAt = expiredAt;
        this.tokenExpireAt = tokenExpiredAt;
        this.setupTimerConfigs(expiredAt, tokenExpiredAt);
      }
    } catch {
      this.resetExpirationData();
    }
  }

  private async fetchProblemData(): Promise<void> {
    try {
      const res = await this.apiClient.getProblemById(
        this.problemId,
        this.invitedToken()
      );
      if (res) this.problem.set(res);
    } catch {
      //
    }
  }

  private async setUpComponents(): Promise<void> {
    this.setUpSelectComponent();
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.createTimerComponents(); // re-bind when timer signals change
        const selected = this.selectedLanguage();
        if (selected.value && selected.label) this.onLanguageChange();
      });
    });
    this.setUpCodeEditorComponent();
  }

  // ===== Code state =====
  private setUpSelectComponent(): void {
    const boilerplates = this.problem()?.languageBoilerplate || [];
    this.languageItems.set(
      boilerplates.map(lang => ({
        value: String(lang.langId),
        label: lang.langName,
      }))
    );
    this.selectedLanguage.set(this.languageItems()[0]);
  }

  private async initializeCode(): Promise<void> {
    const problem = this.problem();
    if (!problem?.languageBoilerplate?.length) return;

    try {
      const problemResults = this.getProblemResults();
      const savedProblem = problemResults.find(
        p => p.problemId === this.problemId
      );

      if (savedProblem) this.loadSavedCodeState(savedProblem);
      else this.initializeNewCodeState(problemResults);
    } finally {
      this.disabled.set(false);
    }
  }

  private loadSavedCodeState(savedProblem: ProblemStorage): void {
    const languageItems = this.languageItems();
    const saveLanguage = languageItems.find(
      item => item.value === savedProblem.language
    ) || { value: '', label: '' };
    this.selectedLanguage.set(saveLanguage);
    this.codeEditorLanguage.set(saveLanguage.label || 'javascript');
    this.codeEditorInput.set(savedProblem.code);
  }

  private initializeNewCodeState(problemResults: ProblemStorage[]): void {
    const problem = this.problem();
    if (!problem?.languageBoilerplate?.length) return;

    const defaultLanguage = problem.languageBoilerplate[0];
    const defaultLangId = String(defaultLanguage.langId);
    const defaultCode = defaultLanguage.starterCode;

    this.codeEditorLanguage.set(defaultLanguage.langName);
    this.codeEditorInput.set(defaultCode);

    problemResults.push({
      problemId: this.problemId,
      deadline: this.expireAt?.toISOString() || '',
      code: defaultCode,
      language: defaultLangId,
    });
    localStorage.setItem('eachProblemResult', JSON.stringify(problemResults));
  }

  private autoSaveProblemState(): void {
    try {
      const problemResults = this.getProblemResults();
      const idx = problemResults.findIndex(p => p.problemId === this.problemId);
      if (idx >= 0) {
        problemResults[idx].code = this.codeEditorOutput();
        problemResults[idx].language = this.selectedLanguage().value;
        localStorage.setItem(
          'eachProblemResult',
          JSON.stringify(problemResults)
        );
      }
    } catch {
      //
    }
  }

  private getProblemResults(): ProblemStorage[] {
    try {
      const existing = localStorage.getItem('eachProblemResult');
      return existing ? JSON.parse(existing) : [];
    } catch {
      return [];
    }
  }

  // ===== Code execution =====
  private setUpCodeEditorComponent(): void {
    const host = this.codeEditorContainer();
    if (host && !this.codeEditorRef) {
      this.codeEditorRef = host.createComponent(CodeEditor, {
        bindings: [
          inputBinding('codeInput', this.codeEditorInput),
          inputBinding('language', this.codeEditorLanguage),
          outputBinding('codeOutput', (code: string) => {
            this.codeEditorOutput.set(code);
            this.autoSaveProblemState();
          }),
        ],
      });
    }
  }

  private async executeCodeOperation(
    operation: 'runTest' | 'submitTest'
  ): Promise<void> {
    if (this.running()) return;
    this.prepareCodeExecution();

    // Analytics
    if (operation === 'runTest')
      this.analytics.trackProblemAttempt(this.problemId, 'test');
    else this.analytics.trackProblemAttempt(this.problemId, 'submit');

    const currentCode =
      this.codeEditorRef?.instance.getCode() || this.codeEditorOutput();

    const body = {
      problemId: this.problemId,
      langId: Number(this.selectedLanguage().value),
      code: currentCode,
      invitedToken: this.invitedToken(),
    };

    try {
      const result =
        operation === 'runTest'
          ? await this.apiClient.runTest(this.invitedToken(), body)
          : await this.apiClient.submitTest(this.invitedToken(), body);

      this.testCaseDetail.set(result);
      this.errorMessage.set(null);
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse) this.handleApiError(err, operation);
    } finally {
      this.running.set(false);
    }
  }

  private prepareCodeExecution(): void {
    this.running.set(true);
    this.errorMessage.set(null);
  }

  private handleApiError(
    err: HttpErrorResponse,
    operation: 'runTest' | 'submitTest'
  ): void {
    const operationText = operation === 'runTest' ? 'Run test' : 'Submit test';
    console.error(`${operationText} failed`, err);

    const map: Record<number, string> = {
      0: 'Cannot connect to the server.',
      400: 'Invalid request. Please check your input.',
      401: 'Unauthorized access',
      422: 'There is an error in your code. Please review and fix it.',
      500: 'Internal server error. Please check your code or try again.',
    };

    const msg =
      map[err.status] ||
      `An error occurred (${err.status}): ${err.error?.message || 'Please try again.'}`;
    this.errorMessage.set(msg);
    this.testCaseDetail.set([]);
  }

  // ===== Timers =====
  private setupTimerConfigs(expiredAt: Date, tokenExpiredAt: Date): void {
    this.timerConfig.set({
      type: 'time',
      expireAt: expiredAt,
      onExpire: () => this.onExpired(''),
    });

    this.tokenTimerConfig.set({
      type: 'time',
      expireAt: tokenExpiredAt,
      onExpire: () => this.onExpired('/exam/complete'),
    });
  }

  private createTimerComponents(): void {
    const timerHost = this.timerContainer();
    if (timerHost && !this.timerRef) {
      this.timerRef = timerHost.createComponent(Timer, {
        bindings: [inputBinding('timer', this.timerConfig)],
      });
    }

    const tokenHost = this.tokenTimerContainer();
    if (tokenHost && !this.tokenTimerRef) {
      this.tokenTimerRef = tokenHost.createComponent(Timer, {
        bindings: [inputBinding('timer', this.tokenTimerConfig)],
      });
    }
  }

  private onExpired(path: string): void {
    if (this.hasExpired) return;
    this.hasExpired = true;
    if (path) this.redirectToPath(path);
    else this.redirectToPrevious();
  }

  private async validateExpirationData(): Promise<void> {
    if (!this.expireAt || !this.tokenExpireAt) return this.redirectToPrevious();
    if (this.expireAt < new Date()) return this.redirectToPrevious();
    if (this.tokenExpireAt && this.tokenExpireAt < new Date())
      return this.redirectToPrevious();
  }

  private resetExpirationData(): void {
    this.expireAt = null;
    this.tokenExpireAt = null;
  }

  // ===== Analytics (viewed) =====
  private setUpAnalytics(): void {
    const problemData = this.problem();
    if (problemData?.title) {
      this.analytics.trackProblemViewed(
        this.problemId,
        problemData.title,
        this.codeEditorLanguage() || 'unknown'
      );
    }
  }

  // ===== Utils =====
  private isValidInviteData(parsed: InviteData): boolean {
    return (
      !!parsed?.data?.invitedToken &&
      !!parsed.data.expiredAt &&
      new Date(parsed.data.expiredAt) >= new Date()
    );
  }

  private getBaseCode(languageId: string): string {
    const problem = this.problem();
    if (!problem?.languageBoilerplate) return '';
    const lang = problem.languageBoilerplate.find(
      b => String(b.langId) === languageId
    );
    return lang?.starterCode ?? '';
  }

  private redirectToPath(path: string): void {
    this.router.navigate([path]);
  }

  private redirectToPrevious(): void {
    try {
      const inviteData = localStorage.getItem('inviteData');
      const parsed: InviteData = inviteData ? JSON.parse(inviteData) : null;
      if (parsed && this.isValidInviteData(parsed)) this.location.back();
      else this.redirectToPath('/forbidden');
    } catch {
      this.redirectToPath('/forbidden');
    }
  }

  private cleanup(): void {
    this.timerConfig.set(null);
    this.tokenTimerConfig.set(null);
    this.hasExpired = true;
    this.timerRef?.destroy();
    this.tokenTimerRef?.destroy();
    this.timerRef = null;
    this.tokenTimerRef = null;
  }
}
